import type { FeatureGridBlockPayload } from '@opinix-cloud/shared';

interface DeploymentProps {
  content: FeatureGridBlockPayload;
}

/**
 * Three-card deployment-options grid. Each card's payload encodes
 * everything in a single `body` string:
 *
 *   <tagline>\n<paragraph>\n• <feature1>\n• <feature2>\n…
 *
 * The first non-empty line is the tagline (cyan eyebrow inside the
 * card), the next paragraph is the body, and `• ` lines are the
 * feature checklist. The middle item is highlighted (ring + badge).
 */
export function Deployment({ content }: DeploymentProps): JSX.Element {
  const items = content.items ?? [];
  return (
    <section id="deployment" className="bg-navy-900 py-24 sm:py-32 relative overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0 bg-grid-faint bg-grid-32 opacity-50"
        aria-hidden="true"
      />
      <div className="container-x relative">
        <div className="max-w-2xl">
          {content.eyebrow && <p className="eyebrow">{content.eyebrow}</p>}
          {content.heading && (
            <h2 className="mt-3 section-title-light">{content.heading}</h2>
          )}
          {content.blurb && (
            <p className="mt-5 text-lg text-steel-300 leading-relaxed">
              {content.blurb}
            </p>
          )}
        </div>
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {items.map((o, idx) => {
            const parsed = parseDeploymentBody(o.body);
            const highlighted = idx === 1;
            return (
              <div
                key={o.title}
                className={`card-dark relative flex flex-col ${
                  highlighted ? 'ring-2 ring-cyan-500 bg-white/[0.06]' : ''
                }`}
              >
                {highlighted && (
                  <div className="absolute -top-3 left-6 rounded-full bg-cyan-500 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-navy-900">
                    Most popular
                  </div>
                )}
                <h3 className="text-xl font-extrabold tracking-tight text-white">
                  {o.title}
                </h3>
                {parsed.tagline && (
                  <p className="mt-1 text-sm font-medium text-cyan-300">
                    {parsed.tagline}
                  </p>
                )}
                {parsed.paragraph && (
                  <p className="mt-4 text-sm text-steel-300 leading-relaxed">
                    {parsed.paragraph}
                  </p>
                )}
                {parsed.bullets.length > 0 && (
                  <ul className="mt-6 space-y-2.5 flex-1">
                    {parsed.bullets.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-sm text-steel-200"
                      >
                        <svg
                          aria-hidden="true"
                          viewBox="0 0 20 20"
                          className="mt-0.5 h-4 w-4 flex-shrink-0 text-cyan-400"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.4 7.4a1 1 0 0 1-1.4 0L3.3 9.5a1 1 0 1 1 1.4-1.4l3.9 3.9 6.7-6.7a1 1 0 0 1 1.4 0Z"
                          />
                        </svg>
                        {f}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * Parses the deployment-card body. Conventions:
 *   - First line beginning with "**" (e.g. "**Self-hosted. Air-gap-ready.")
 *     is the cyan tagline (asterisks stripped).
 *   - Subsequent non-bullet lines join into the paragraph.
 *   - Lines beginning with "• " or "- " become bullets.
 */
function parseDeploymentBody(body: string): {
  tagline: string;
  paragraph: string;
  bullets: string[];
} {
  const lines = body.split('\n').filter((l) => l.length > 0);
  let tagline = '';
  let paragraph = '';
  const bullets: string[] = [];
  for (const line of lines) {
    if (line.startsWith('**')) {
      tagline = line.slice(2).trim();
    } else if (line.startsWith('• ') || line.startsWith('- ')) {
      bullets.push(line.replace(/^[•\-]\s*/, '').trim());
    } else if (!paragraph) {
      paragraph = line;
    } else {
      paragraph += ' ' + line;
    }
  }
  return { tagline, paragraph, bullets };
}
